import { useState, useRef, useEffect } from "react";
import {
  Tree,
  getBackendOptions,
  MultiBackend,
} from "@minoru/react-dnd-treeview";
import { DndProvider } from "react-dnd";
import { Tag, Input, IconButton, Whisper, Tooltip, Button, Uploader, toaster, Message } from "rsuite";

import ArrowDownLineIcon from '@rsuite/icons/ArrowDownLine';
import AttachmentIcon from '@rsuite/icons/Attachment';
import TrashIcon from '@rsuite/icons/Trash';
import UndoIcon from '@rsuite/icons/Reload';

import CollaspedFillIcon from '@rsuite/icons/CollaspedFill';
import ThreeColumnsIcon from '@rsuite/icons/ThreeColumns';

import { updateBrief, updateBriefSection, deleteFile } from "../briefs/briefCreationManager";
import { useParams } from "react-router-dom";
import { formatTimestamp } from '../../utils/formatTimestamp';

const API_HOST = process.env.REACT_APP_API_HOST;

const ITEM_TYPES = {
    section: { label: 'Раздел', color: 'green' },
    parameter: { label: 'Параметр', color: 'yellow' },
};

function computeNumbers(items) {
  const numbers = {};
  const rootItems = items.filter(i => i.parent === 0).sort((a, b) => {
    if (a.data.index !== b.data.index) return a.data.index - b.data.index;
    return a.data.updated_at > b.data.updated_at ? -1 : 1;
  });
  rootItems.forEach((item, idx) => {
    const n = idx + 1;
    numbers[item.id] = String(n);
    if (item.droppable) {
      const children = items.filter(i => i.parent === item.id).sort((a, b) => {
        if (a.data.index !== b.data.index) return a.data.index - b.data.index;
        return a.data.updated_at > b.data.updated_at ? -1 : 1;
      });
      children.forEach((child, cIdx) => {
        numbers[child.id] = `${n}.${cIdx + 1}`;
      });
    }
  });
  return numbers;
}

const renderTreeNode = (
  item, onToggle, depth, isOpen,
  existingFiles, pendingUploads, setPendingUploads,
  pendingDeletions, setPendingDeletions,
  inputDraft, setInputDraft,
  numbers, greyedIds
) => {
    const isGreyed = greyedIds.has(item.id);
    const greyStyle = isGreyed ? { opacity: 0.45, pointerEvents: 'none' } : {};
    const myExisting = existingFiles[item.id] || [];
    const myPending = pendingUploads[item.id] || [];

    return (
        <div className={`brief-list-item-wrapper`} style={{marginBottom: '0.3rem'}}>
        <div className={`brief-list-item-box view`} style={{marginLeft: 2 * depth + "rem", ...greyStyle}}>
            <div style={{display: 'flex', alignItems: 'center', width: '100%', height: "fit-content", borderRadius: "0.5rem", backgroundColor: 'var(--rs-gray-50)'}}>
                {numbers && numbers[item.id] && (
                  <span style={{
                    minWidth: '2rem', fontWeight: 600,
                    color: 'var(--rs-text-secondary)', marginLeft: '0.4rem',
                    marginRight: '0.35rem', fontSize: '0.85em', flexShrink: 0
                  }}>{numbers[item.id]}</span>
                )}
                <Tag className={"brief-tree-tag"} onClick={onToggle} style={{marginRight: 10}} color={ITEM_TYPES[item.data.type].color}>
                    <span className={`brief-tree-tag-label ${item.droppable ? "expandable" : ""}`}>
                      {ITEM_TYPES[item.data.type].label}</span>
                    {item.droppable &&
                        <ArrowDownLineIcon className={"brief-tree-tag-icon"} style={{transform: `rotate(${isOpen ? 0 : -90}deg)`}} />
                    }
                </Tag>
                {item.data.have_ocr ? <Whisper placement="top" trigger="hover" controlId="control-id-hover" speaker={<Tooltip>OCR</Tooltip>}><ThreeColumnsIcon /></Whisper> : null}
                <span style={{width: '100%', padding: '0.25rem 0.5rem', userSelect: 'text', cursor: 'text'}}>
                  {item.text}
                </span>
            </div>
            <div style={{marginLeft: '0.25rem', display: 'flex', alignItems: 'center'}} />
        </div>

        {item.data.type === "parameter" && item.data.have_file && !isGreyed && (
          <div style={{
            marginLeft: 2 * (depth + 1) + "rem",
            width: `calc(100% - ${2 * (depth + 1)}rem)`,
            display: 'flex', flexDirection: 'column', gap: '0.4rem',
          }}>
            {/* existing DB files */}
            {myExisting.length > 0 && (
              <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                {myExisting.map(file => {
                  const isPending = pendingDeletions.has(file.fileKey);
                  return (
                    <div key={file.fileKey} style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85em'}}>
                      <AttachmentIcon style={{color: 'var(--rs-text-secondary)', flexShrink: 0}} />
                      <span style={{flex: 1, textDecoration: isPending ? 'line-through' : 'none', opacity: isPending ? 0.5 : 1}}>
                        {file.name}
                      </span>
                      {file.uploadedAt && (
                        <span style={{fontSize: '0.8em', color: 'var(--rs-text-secondary)'}}>
                          {formatTimestamp(file.uploadedAt)}
                        </span>
                      )}
                      {isPending ? (
                        <Button size="xs" appearance="subtle" color="green" startIcon={<UndoIcon />}
                          onClick={() => setPendingDeletions(prev => {
                            const next = new Set(prev); next.delete(file.fileKey); return next;
                          })}
                        >Отменить</Button>
                      ) : (
                        <IconButton size="xs" color="red" appearance="subtle"
                          icon={<TrashIcon />}
                          onClick={() => setPendingDeletions(prev => new Set([...prev, file.fileKey]))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* new files staged for upload */}
            {myPending.length > 0 && (
              <div style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                {myPending.map((f, idx) => (
                  <div key={idx} style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85em'}}>
                    <AttachmentIcon style={{color: 'var(--rs-cyan-500)', flexShrink: 0}} />
                    <span style={{flex: 1}}>{f.name}</span>
                    <Tag size="sm" color="cyan">Новый</Tag>
                    <IconButton size="xs" color="red" appearance="subtle"
                      icon={<TrashIcon />}
                      onClick={() => setPendingUploads(prev => ({
                        ...prev,
                        [item.id]: (prev[item.id] || []).filter((_, i) => i !== idx)
                      }))}
                    />
                  </div>
                ))}
              </div>
            )}

            <Uploader
              autoUpload={false}
              fileListVisible={false}
              multiple
              fileList={pendingUploads[item.id] || []}
              onChange={(fileList) => {
                setPendingUploads(prev => ({...prev, [item.id]: fileList}));
              }}
            >
              <Button startIcon={<AttachmentIcon />} appearance="ghost" size="sm">
                Прикрепить файлы
              </Button>
            </Uploader>
          </div>
        )}

        {item.data.have_input && !isGreyed && (
          <div className="brief-list-item-input" style={{marginLeft: 2 * (depth + 1) + "rem", width: `calc(100% - ${2 * (depth + 1)}rem)`}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem'}}>
              <Tag className="brief-list-item-input-label" color="orange">
                Необходим ввод
              </Tag>
              {item.data.input_text && inputDraft[item.id] === undefined && (
                <span style={{fontSize: '0.75em', color: 'var(--rs-text-secondary)'}}>
                  Отправлено: {formatTimestamp(item.data.updated_at)}
                </span>
              )}
            </div>
            <Input
              value={inputDraft[item.id] !== undefined ? inputDraft[item.id] : (item.data.input_text || '')}
              onChange={(text) => setInputDraft(prev => ({...prev, [item.id]: text}))}
              className="brief-list-item-input-input"
            />
          </div>
        )}
        </div>
    );
};

function App({ items }) {
  const ref = useRef(null);
  const { key } = useParams();

  const [existingFiles, setExistingFiles] = useState({});
  const [pendingUploads, setPendingUploads] = useState({});
  const [pendingDeletions, setPendingDeletions] = useState(new Set());
  const [inputDraft, setInputDraft] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setExistingFiles(items.reduce((acc, item) => ({
      ...acc,
      [item.id]: (item.data.files || []).map(f => ({
        name: f.filename,
        fileKey: f.id,
        uploadedAt: f.uploaded_at,
      }))
    }), {}));
  }, [items]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // 1. upload new files
      for (const [paramId, fileList] of Object.entries(pendingUploads)) {
        if (!fileList.length) continue;
        const newDbFiles = [];
        for (const fileEntry of fileList) {
          if (!fileEntry.blobFile) continue;
          const formData = new FormData();
          formData.append('file', fileEntry.blobFile);
          const res = await fetch(`${API_HOST}/api/parameters/${paramId}/files`, {
            method: 'POST',
            body: formData,
          });
          if (res.ok) {
            const uploaded = await res.json();
            const list = Array.isArray(uploaded) ? uploaded : [uploaded];
            list.forEach(f => newDbFiles.push({name: f.filename, fileKey: f.id, uploadedAt: f.uploaded_at}));
          }
        }
        if (newDbFiles.length > 0) {
          setExistingFiles(prev => ({
            ...prev,
            [paramId]: [...(prev[paramId] || []), ...newDbFiles],
          }));
        }
      }
      setPendingUploads({});

      // 2. save text drafts
      for (const [paramId, text] of Object.entries(inputDraft)) {
        await updateBriefSection(Number(paramId), 'parameter', {input_text: text});
      }
      setInputDraft({});

      // 3. delete staged files
      const deleted = new Set();
      for (const fileId of pendingDeletions) {
        const res = await deleteFile(fileId).catch(() => null);
        if (res && res.ok) deleted.add(fileId);
      }
      if (deleted.size > 0) {
        setExistingFiles(prev => {
          const next = {...prev};
          for (const paramId of Object.keys(next)) {
            next[paramId] = (next[paramId] || []).filter(f => !deleted.has(f.fileKey));
          }
          return next;
        });
      }
      setPendingDeletions(new Set());

      // 4. mark brief as submitted
      await updateBrief(key, {status: "waiting"});

      toaster.push(
        <Message type="success" showIcon closable>Данные успешно отправлены</Message>,
        { placement: 'topCenter', duration: 4000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const numbers = computeNumbers(items);

  const greyedIds = new Set();
  items.forEach(item => {
    if (item.data.approved) {
      greyedIds.add(item.id);
      if (item.droppable) {
        items.filter(i => i.parent === item.id).forEach(child => greyedIds.add(child.id));
      }
    }
  });

  const hasDraft = Object.values(pendingUploads).some(l => l.length > 0)
    || pendingDeletions.size > 0
    || Object.keys(inputDraft).length > 0;

  return (
    <div className="brief-tree">
      <div className="brief-tree-header">
        <span>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Свернуть всё</Tooltip>}>
            <IconButton className="brief-tree-header-icon" onClick={() => ref.current?.closeAll()} icon={<CollaspedFillIcon />}/>
          </Whisper>
          <Whisper placement="top" trigger="hover" speaker={<Tooltip>Развернуть всё</Tooltip>}>
            <IconButton className="brief-tree-header-icon" onClick={() => ref.current?.openAll()} icon={<ArrowDownLineIcon />}/>
          </Whisper>
        </span>
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
          {hasDraft && <Tag color="orange">Есть несохранённые изменения</Tag>}
          <Button size="sm" appearance="primary" color="green" loading={submitting} onClick={handleSubmit}>
            Отправить
          </Button>
        </div>
      </div>
      <DndProvider backend={MultiBackend} options={getBackendOptions()}>
        <Tree
          enableAnimateExpand={true}
          ref={ref}
          tree={items}
          rootId={0}
          classes={{
            root: 'brief-tree-root',
            dropTarget: 'brief-tree-drop-target',
            draggingSource: 'draggingSource',
          }}
          insertDroppableFirst={false}
          initialOpen
          canDrag={() => false}
          sort={false}
          render={(node, { depth, isOpen, onToggle }) => renderTreeNode(
            node, onToggle, depth, isOpen,
            existingFiles, pendingUploads, setPendingUploads,
            pendingDeletions, setPendingDeletions,
            inputDraft, setInputDraft,
            numbers, greyedIds
          )}
        />
      </DndProvider>
    </div>
  );
}

export default App;
